// App.jsx - Oppdatert med DesignSystemProvider
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { DesignSystemProvider, useDesignSystem, cn } from './designSystem';


// Shadcn/UI component imports
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Lucide icons med LogOut-ikonet lagt til
import {
  Mic, MicOff, BookOpen, Settings as SettingsIcon, Plus, Archive,
  ChevronRight, ChevronLeft, Brain, FileText, Calendar, Clock,
  Copy, Check, AlertCircle, Sparkles, Save, Edit, Trash, Lock, Unlock, Menu, X,
  LogOut
} from "lucide-react";

// Importerer login-funksjonalitet
import Login from './Login';
import { logout, isAuthenticated as authServiceIsAuthenticated, getCurrentUser } from './authService';

import UsageLimitModal from './UsageLimitModal';
import Registration from './Registration';
import { updateUserUsage, checkUserLimits } from './authService';

// templateEditor-komponentene
import TemplateDialogForm from "./components/TemplateEditor/TemplateDialogForm";
// Miljøkonfigurasjon – flyttet inn i komponenten
import { useEnvironment } from './EnvironmentProvider';

// SettingsDialog – importeres som egen fil
import SettingsDialog from './components/SettingsDialog';

import LoadingSpinner from './components/LoadingSpinner';

// Importerer ResourceBank og ResourceSuggestions
import ResourceBank from './ResourceBank';
import ResourceSuggestions from './ResourceSuggestions';

import MobileHeader from './components/mobile/MobileHeader';
import MobileNavigation from './components/mobile/MobileNavigation';

import useSocket from './hooks/useSocket';
import { convertMarkdownToHtml } from './utils/textUtils';

// Import service-funksjonene
import { 
  createNewKonsultasjon as createNewKonsultasjonService,  // Legg til "Service" i navnet
  updateKonsultasjon as updateKonsultasjonService,
  deleteKonsultasjon as deleteKonsultasjonService,
  deleteAllKonsultasjoner as deleteAllKonsultasjonerService,
  fetchKonsultasjoner,
  generateNote as generateNoteService
} from './services/konsultasjonService';
// Import mal-funksjonene
import { 
  fetchMaler, 
  updateMal, 
  deleteMal 
} from './services/malService';

// import TranscriptionTab
import TranscriptionTab from './components/tabs/TranscriptionTab';

import NoteTab from './components/tabs/NoteTab';

import Sidebar from './components/layout/Sidebar';

import TemplateTab from './components/tabs/TemplateTab';

import { languageOptions, setTranscriptionLanguage as setLanguageService } from './services/languageService';
import { 
  toggleRecording as toggleRecordingService,
  startRecording,
  stopRecording
} from './services/audioRecordingService';

import useConsultation from './hooks/useConsultation';
import useTemplates from './hooks/useTemplates';

// MobileTabs-komponenten kan også oppdateres med designsystem, men det ville gjort
// denne filen veldig stor. Vi kan gjøre det som et separat steg.

// Konstanter
const DEFAULT_TEMPLATE = "Oppfølgingsnotat";
const DEFAULT_GPT_MODEL = "gpt-4o";
const MOBILE_BREAKPOINT = 640;


// Hovedkomponent med DesignSystemProvider
function AppContent() {
  // Hent miljøvariablene
  const { API_BASE_URL, WEBSOCKET_URL, userId, updateUserId } = useEnvironment();
  const { colors, typography, componentStyles, effects, shadows, isDarkMode, setIsDarkMode } = useDesignSystem();

  const socket = useSocket(WEBSOCKET_URL);


  const [config, setConfig] = useState({});
  
  // Now use config after it's been initialized
  const {
    konsultasjoner,
    currentKonsultasjon,
    setCurrentKonsultasjon,
    transcription,
    setTranscription,
    note,
    setNote,
    contextText,
    setContextText,
    isGeneratingNote,
    isTranscriptionLocked,
    setIsTranscriptionLocked,
    createNewKonsultasjon,
    updateKonsultasjon,
    handleDeleteKonsultasjon,
    handleDeleteAllKonsultasjoner,
    generateNote: generateNoteFromHook
  } = useConsultation(API_BASE_URL, userId, socket, config);  



  const {
    templates,
    chosenTemplate,
    setChosenTemplate,
    editingTemplate,
    setEditingTemplate,
    searchQuery,
    setSearchQuery,
    isTemplateDialogOpen: isDialogOpen,
    setIsTemplateDialogOpen: setIsDialogOpen,
    isTemplateSaving,
    openNewTemplateDialog,
    openEditTemplateDialog,
    handleSaveTemplate,
    handleDeleteTemplate,
    filteredTemplates
  } = useTemplates(API_BASE_URL, userId, DEFAULT_TEMPLATE);

  const handleLoginSuccess = (userData) => {
    setUsername(userData.username);
    setCurrentUser(userData);
    setIsAuthenticated(true);
    setShowRegistration(false);
    
    // Use updateUserId that was retrieved at the component level
    updateUserId(userData.userId);
  };

    // Define handleUpdateTemplate function
    const handleUpdateTemplate = async (title, content, oldTitle = null) => {
      try {
        if (oldTitle) {
          // Update existing template - pass content directly as string
          await updateMal(API_BASE_URL, userId, title, content, oldTitle);
        } else {
          // Create new template - pass title and content as separate parameters
          await handleSaveTemplate(title, content);
        }
      } catch (error) {
        console.error("Error updating template:", error);
        throw error;
      }
    };

  

  // Rest of state declarations
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [activeTab, setActiveTab] = useState('transkripsjon');
  const [showResourceSuggestions, setShowResourceSuggestions] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [gptModels] = useState([ "gpt-4o" ]);
  const [transcriptionLanguage, setTranscriptionLanguage] = useState("nb-NO");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [audioProcessor, setAudioProcessor] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [isTabletView, setIsTabletView] = useState(window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < 1024);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
  const isDesktopView = !isMobileView && !isTabletView;
  // Add these state variables in your App component
  const [showRegistration, setShowRegistration] = useState(false);
  const [showUsageLimitModal, setShowUsageLimitModal] = useState(false);
  const [limitType, setLimitType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Autentiseringssjekk
  // Autentiseringssjekk
useEffect(() => {
  if (authServiceIsAuthenticated()) {
    const user = getCurrentUser();
    setUsername(user.username);
    setCurrentUser(user);
    setIsAuthenticated(true);
  }
}, []);

  useEffect(() => {
    if (userId && isAuthenticated) {
      console.log(`User logged in: ${username || 'Loading username...'} (${userId}). Refreshing data...`);
      
      // Add a check to make sure username is loaded
      if (!username) {
        const storedUsername = localStorage.getItem('skriboUsername');
        if (storedUsername) {
          setUsername(storedUsername);
        }
      }
    }
  }, [userId, isAuthenticated]);
  // Mobilvisning og orientering – sjekk om vi er i mobil-/tabletvisning og liggende/stående orientering
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setIsMobileView(width < MOBILE_BREAKPOINT);
      setIsTabletView(width >= MOBILE_BREAKPOINT && width < 1024);
      setIsLandscape(width > height && width < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Room management
  useEffect(() => {
    if (!socket || !currentKonsultasjon) return;
    socket.emit('join_room', { konsultasjon_id: currentKonsultasjon.id });
    return () => {
      socket.emit('leave_room', { konsultasjon_id: currentKonsultasjon.id });
    };
  }, [socket, currentKonsultasjon]);

  useEffect(() => {
    if (!socket) return;
    const handleReconnect = () => {
      if (currentKonsultasjon) {
        socket.emit('join_room', { konsultasjon_id: currentKonsultasjon.id });
      }
    };
    socket.on('connect', handleReconnect);
    return () => socket.off('connect', handleReconnect);
  }, [socket, currentKonsultasjon]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;
    
    // Handle error and other non-transcription events
    socket.on('stream_started', data => console.log('Stream started:', data));
    socket.on('stream_stopped', data => console.log('Stream stopped:', data));
    socket.on('error', error => {
      console.error('Socket error:', error);
      alert('Det oppstod en feil med lydstrømmen: ' + error.error);
    });
  
    return () => {
      if (mediaRecorder) {
        mediaRecorder.stop();
      }
      socket.off('stream_started');
      socket.off('stream_stopped');
      socket.off('error');
    };
  }, [socket, mediaRecorder]);

  // Polling for transcription
  useEffect(() => {
    if (!currentKonsultasjon || !userId) return;
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/konsultasjoner/${currentKonsultasjon.id}/transcript`, {
          headers: {
            'X-User-Id': userId
          }
        });
        if (isTranscriptionLocked && res.data.transcript !== transcription) {
          setTranscription(res.data.transcript);
        }
      } catch (error) {
        console.error('Error fetching transcript:', error);
      }
    }, 1000);
    return () => clearInterval(pollInterval);
  }, [currentKonsultasjon, API_BASE_URL, transcription, isTranscriptionLocked, userId]);

  // Hotkeys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && !e.ctrlKey) {
        const key = e.key.toLowerCase();
        if (key === 'n') {
          e.preventDefault();
          startNewConsultation();
        } else if (key === 'g') {
          e.preventDefault();
          generateNote();
        } else if (key === 'd') {
          e.preventDefault();
          if (currentKonsultasjon) {
            handleDeleteKonsultasjon(currentKonsultasjon);
          }
        } else if (key === 'r') {
          e.preventDefault();
          setShowResourceSuggestions(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRecording, currentKonsultasjon]);

  useEffect(() => {
    if (currentKonsultasjon) {
      setContextText(currentKonsultasjon.kontekst || "");
      setNote(currentKonsultasjon.notat || "");
    } else {
      setContextText("");
      setNote("");
    }
  }, [currentKonsultasjon?.id]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
    
  const checkRecordingLimits = () => {
    if (currentUser?.userType === 'demo') {
      const { hasReachedLimit, limitType } = checkUserLimits();
      if (hasReachedLimit && limitType === 'whisper_time') {
        setLimitType('whisper_time');
        setShowUsageLimitModal(true);
        return false;
      }
    }
    return true;
  };
  
// Check if user can generate notes
const checkNoteLimits = () => {
  if (currentUser?.userType === 'demo') {
    const { hasReachedLimit, limitType } = checkUserLimits();
    if (hasReachedLimit && limitType === 'note_generation') {
      setLimitType('note_generation');
      setShowUsageLimitModal(true);
      return false;
    }
  }
  return true;
};

  // Funksjon for å oppdatere språk
  const handleLanguageChange = async (newLanguage) => {
    try {
      // Make sure to pass userId to the language service
      await setLanguageService(API_BASE_URL, newLanguage, userId);
      setTranscriptionLanguage(newLanguage);
    } catch (error) {
      console.error('Error changing language:', error);
      alert('Kunne ikke endre språk. Prøv igjen senere.');
    }
  };

  // Generer notat
  const generateNote = async () => {
    try {
      // Check limits before generating
      if (!checkNoteLimits()) {
        return null;
      }
      
      const result = await generateNoteFromHook(
        chosenTemplate,
        templates, 
        DEFAULT_GPT_MODEL
      );
      
      // Update usage after successful generation
      if (result && currentUser?.userType === 'demo') {
        const newUsage = updateUserUsage('note_generation', 1);
        setCurrentUser({...currentUser, notes_generated: newUsage});
      }
      
      setActiveTab('notat');
      return result;
    } catch (error) {
      console.error("Error in generateNote:", error);
    }
  };

 // Stopp/start opptak
const toggleRecordingForCurrent = async () => {
  if (isRecording) {
    // When stopping, update the usage
    if (currentUser?.userType === 'demo') {
      // Approximate recording time (let's say 1 minute for simplicity)
      const recordingTime = 1;
      const newUsage = updateUserUsage('whisper_time', recordingTime);
      setCurrentUser({...currentUser, whisper_minutes_used: newUsage});
    }
    
    await toggleRecordingService(
      isRecording,
      socket,
      currentKonsultasjon,
      audioContext,
      audioStream,
      startNewConsultation,
      setActiveTab,
      setIsRecording,
      setAudioContext,
      setAudioStream
    );
  } else {
    // Check limits before starting
    if (checkRecordingLimits()) {
      await toggleRecordingService(
        isRecording,
        socket,
        currentKonsultasjon,
        audioContext,
        audioStream,
        startNewConsultation,
        setActiveTab,
        setIsRecording,
        setAudioContext,
        setAudioStream
      );
    }
  }
};
 // Start ny konsultasjon
const startNewConsultation = async () => {
  try {
    // If recording is in progress, stop it properly using the service
    if (isRecording && currentKonsultasjon) {
      await stopRecording(audioContext, audioStream, socket, currentKonsultasjon.id);
      setAudioContext(null);
      setAudioStream(null);
      setIsRecording(false);
    }
    
    // Reset language to Norwegian
    setTranscriptionLanguage("nb-NO");
    await handleLanguageChange("nb-NO");
    
    // Create new consultation
    const newConsultation = await createNewKonsultasjon(
      "", 
      chosenTemplate, 
      config.gpt_model_choice || DEFAULT_GPT_MODEL
    );
    setCurrentKonsultasjon(newConsultation);
    
    try {
      // Start recording using the service (no need to call getUserMedia separately)
      const { audioContext: newContext, audioStream: newStream } = await startRecording(
        socket, 
        newConsultation.id
      );
      
      setAudioContext(newContext);
      setAudioStream(newStream);
      setIsRecording(true);
      setActiveTab('transkripsjon');
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Kunne ikke starte opptak: " + error.message);
    }
  } catch (error) {
    console.error("Error starting new consultation:", error);
    alert("Kunne ikke starte ny konsultasjon: " + error.message);
  }
};

  

    const handleRegisterSuccess = (userData) => {
      setUsername(userData.username);
      setCurrentUser(userData);
      setIsAuthenticated(true);
      setShowRegistration(false);
    };

    const handleLogout = () => {
      // Instead of trying to set konsultasjoner directly, use the exposed functions
      // Clear current consultation
      setCurrentKonsultasjon(null);
      setTranscription('');
      setNote('');
      setContextText('');
      
      // Logout (clears localStorage)
      logout();
      
      // Reset component state
      setUsername('');
      setCurrentUser(null);
      setIsAuthenticated(false);
      
      // Use updateUserId that was retrieved at the component level
      updateUserId(null);
    };

  // Dersom brukeren ikke er logget inn, vis Login-komponenten
if (!isAuthenticated) {
  return showRegistration ? (
    <Registration 
      onRegisterSuccess={handleRegisterSuccess}
      onSwitchToLogin={() => setShowRegistration(false)}
    />
  ) : (
    <Login 
      onLoginSuccess={handleLoginSuccess}
      onSwitchToRegistration={() => setShowRegistration(true)}
    />
  );
}

  return (
    <div className="min-h-screen">
      <div className={cn(
        "min-h-screen",
        "bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900"
      )}>
      {/* Resten av koden for både mobil- og desktop-visning */}
      {isMobileView ? (
        <div className="h-screen flex flex-col overflow-hidden">
          {/* Mobile Header */}
          <MobileHeader 
            setIsMobileMenuOpen={setIsMobileMenuOpen} 
            setIsSettingsOpen={setIsSettingsOpen}
            username={username}
            handleLogout={handleLogout}
            isLandscape={isLandscape}
          />

          <MobileNavigation 
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            konsultasjoner={konsultasjoner}
            currentKonsultasjon={currentKonsultasjon}
            setCurrentKonsultasjon={setCurrentKonsultasjon}
            startNewConsultation={startNewConsultation}
            setTranscription={setTranscription}
            setNote={setNote}
            setChosenTemplate={setChosenTemplate}
            DEFAULT_TEMPLATE={DEFAULT_TEMPLATE}
            isLandscape={isLandscape}
          />

          {/* Resten av mobilvisningen... */}
            <main className="flex-1 overflow-hidden">
              <div className="h-full p-4">
                {currentKonsultasjon ? (
                  <Tabs 
                    value={activeTab} 
                    onValueChange={setActiveTab} 
                    className="space-y-3"
                  >
                    {/* Faneliste */}
                    <div className={cn(
                      effects.glassmorphism,
                      "rounded-lg shadow-sm"
                    )}>
                      <TabsList className="w-full grid grid-cols-4 gap-0 p-1.5 h-14 bg-transparent">
                        <TabsTrigger 
                          value="transkripsjon" 
                          className={cn(
                            "flex flex-col items-center justify-center py-1.5", 
                            "data-[state=active]:bg-gradient-to-r",
                            "data-[state=active]:from-blue-500",
                            "data-[state=active]:to-indigo-500",
                            "data-[state=active]:text-white rounded-md"
                          )}
                        >
                          <Mic className="h-4 w-4 mb-0.5" />
                          <span className="text-xs">Transkripsjon</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="notat" 
                          className={cn(
                            "flex flex-col items-center justify-center py-1.5", 
                            "data-[state=active]:bg-gradient-to-r",
                            "data-[state=active]:from-blue-500",
                            "data-[state=active]:to-indigo-500",
                            "data-[state=active]:text-white rounded-md"
                          )}
                        >
                          <FileText className="h-4 w-4 mb-0.5" />
                          <span className="text-xs">Notat</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="maler" 
                          className={cn(
                            "flex flex-col items-center justify-center py-1.5", 
                            "data-[state=active]:bg-gradient-to-r",
                            "data-[state=active]:from-blue-500",
                            "data-[state=active]:to-indigo-500",
                            "data-[state=active]:text-white rounded-md"
                          )}
                        >
                          <Save className="h-4 w-4 mb-0.5" />
                          <span className="text-xs">Maler</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="ressurser" 
                          className={cn(
                            "flex flex-col items-center justify-center py-1.5", 
                            "data-[state=active]:bg-gradient-to-r",
                            "data-[state=active]:from-blue-500",
                            "data-[state=active]:to-indigo-500",
                            "data-[state=active]:text-white rounded-md"
                          )}
                        >
                          <BookOpen className="h-4 w-4 mb-0.5" />
                          <span className="text-xs">Ressurser</span>
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    {/* Transkripsjonsfane */}
                    <TabsContent value="transkripsjon" className="mt-2">
                      <TranscriptionTab
                        transcription={transcription}
                        setTranscription={setTranscription}
                        currentKonsultasjon={currentKonsultasjon}
                        updateKonsultasjon={updateKonsultasjon}
                        isRecording={isRecording}
                        isTranscriptionLocked={isTranscriptionLocked}
                        setIsTranscriptionLocked={setIsTranscriptionLocked}
                        contextText={contextText}
                        setContextText={setContextText}
                        isLandscape={isLandscape}
                        transcriptionLanguage={transcriptionLanguage}
                        handleLanguageChange={handleLanguageChange}
                        languageOptions={languageOptions}
                        generateNote={generateNote}
                        isGeneratingNote={isGeneratingNote}
                        toggleRecordingForCurrent={toggleRecordingForCurrent}
                        isTabletView={false}
                        isDesktopView={false}
                      />
                    </TabsContent>

                    {/* Notatfane */}
                    <TabsContent value="notat" className="mt-2">
                      <NoteTab
                        note={note}
                        chosenTemplate={chosenTemplate}
                        setChosenTemplate={setChosenTemplate}
                        templates={templates}
                        generateNote={generateNote}
                        isGeneratingNote={isGeneratingNote}
                        convertMarkdownToHtml={convertMarkdownToHtml}
                        setShowResourceSuggestions={setShowResourceSuggestions}
                        isTabletView={false}
                        isDesktopView={false}
                        isLandscape={isLandscape}
                      />
                    </TabsContent>

              
{/* Malerfane */}
                  <TabsContent value="maler" className="mt-2">
                    <TemplateTab 
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      templates={templates}
                      setEditingTemplate={setEditingTemplate}
                      handleDeleteTemplate={handleDeleteTemplate}
                      setChosenTemplate={setChosenTemplate}
                      setActiveTab={setActiveTab}
                      isMobileView={true}
                      setIsDialogOpen={setIsDialogOpen}
                      effects={effects}
                    />
                  </TabsContent>
            


                    {/* Ressursfane */}
                    <TabsContent value="ressurser" className="mt-2">
                      <div className={cn(
                        "h-[calc(100vh-185px)]",
                        effects.glassmorphism,
                        "rounded-lg border",
                        colors.neutral.border,
                        "overflow-hidden"
                      )}>
                        <ResourceBank />
                      </div>
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center space-y-4">
                      <div className={cn(
                        "inline-flex items-center justify-center w-12 h-12 rounded-full",
                        colors.primary.light
                      )}>
                        <FileText className={cn(
                          "h-6 w-6",
                          colors.primary.text
                        )} />
                      </div>
                      <h3 className={cn(
                        typography.h3,
                        colors.neutral.textPrimary
                      )}>
                        Ingen aktiv konsultasjon
                      </h3>
                      <p className={cn(
                        typography.small,
                        colors.neutral.textTertiary
                      )}>
                        Start en ny konsultasjon for å begynne
                      </p>
                      <Button
                        onClick={startNewConsultation}
                        className={cn(
                          "w-full",
                          componentStyles.button.base,
                          componentStyles.button.primary
                        )}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Start ny konsultasjon
                      </Button>
                    </div>
                  </div>
                )}
              </div>
          
              {/* Floating Action Buttons */}
              <div className={cn(
                "fixed z-50",
                isLandscape 
                  ? "bottom-2 right-2 flex flex-row gap-2" 
                  : "bottom-6 right-6 flex flex-col gap-2"
              )}>
                <Button
                  onClick={generateNote}
                  className={cn(
                    isLandscape ? "rounded-lg w-10 h-10" : "rounded-full w-12 h-12",
                    componentStyles.button.primary
                  )}
                  disabled={isGeneratingNote}
                >
                  <FileText className={isLandscape ? "h-5 w-5" : "h-6 w-6"} />
                </Button>
                <Button
                  onClick={toggleRecordingForCurrent}
                  className={cn(
                    isLandscape ? "rounded-lg w-10 h-10" : "rounded-full w-12 h-12", 
                    isRecording 
                      ? colors.status.recording 
                      : componentStyles.button.primary
                  )}
                >
                  {isRecording ? (
                    <MicOff className={isLandscape ? "h-5 w-5" : "h-6 w-6"} />
                  ) : (
                    <Mic className={isLandscape ? "h-5 w-5" : "h-6 w-6"} />
                  )}
                </Button>
              </div>
            </main>  
           
                </div>
        ) : (
          <div className="flex h-screen">
            {/* Desktop Sidebar - Redesigned */}
            <Sidebar
  isSidebarOpen={isSidebarOpen}
  setIsSidebarOpen={setIsSidebarOpen}
  isTabletView={isTabletView}
  username={username}
  setIsSettingsOpen={setIsSettingsOpen}
  handleLogout={handleLogout}
  startNewConsultation={startNewConsultation}
  handleDeleteAllKonsultasjoner={handleDeleteAllKonsultasjoner}
  konsultasjoner={konsultasjoner}
  currentKonsultasjon={currentKonsultasjon}
  setCurrentKonsultasjon={setCurrentKonsultasjon}
  setTranscription={setTranscription}
  setNote={setNote}
  setChosenTemplate={setChosenTemplate}
  DEFAULT_TEMPLATE={DEFAULT_TEMPLATE}
  handleDeleteKonsultasjon={handleDeleteKonsultasjon}
/>
          {/* Main Content Area */}
          <div className="flex-1 overflow-hidden">
            <div className={cn(
              "p-6 h-full flex flex-col",
              isTabletView && "p-4"
            )}>
              <div className={cn(
                "mb-6",
                isTabletView && "mb-4"
              )}>
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className={cn(
                      "text-2xl font-semibold",
                      "text-gray-900 dark:text-white", // Hvit tekst i dark mode i stedet for gradient
                      "transition-colors", // Jevn overgang mellom moduser
                      isTabletView && "text-xl"
                    )}>
                      {currentKonsultasjon ? (currentKonsultasjon.title || "Ny konsultasjon") : "Ingen konsultasjon valgt"}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-neutral-400 mt-2">
                      <span className={cn(
                        "flex items-center",
                        "bg-gray-100 dark:bg-neutral-800", // Grå bakgrunn i stedet for blå
                        "px-3 py-1 rounded-full"
                      )}>
                        <Calendar className={cn(
                          "h-4 w-4 mr-2",
                          "text-gray-600 dark:text-neutral-400" // Grå ikon i stedet for blå
                        )} />
                        {currentKonsultasjon ? new Date(currentKonsultasjon.start_time).toLocaleDateString() : new Date().toLocaleDateString()}
                      </span>
                      <span className={cn(
                        "flex items-center",
                        "bg-gray-100 dark:bg-neutral-800", // Grå bakgrunn i stedet for blå
                        "px-3 py-1 rounded-full"
                      )}>
                        <Clock className={cn(
                          "h-4 w-4 mr-2",
                          "text-gray-600 dark:text-neutral-400" // Grå ikon i stedet for blå
                        )} />
                        {currentKonsultasjon ? new Date(currentKonsultasjon.start_time).toLocaleTimeString() : new Date().toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
                <TabsList className={cn(
                  "w-full grid grid-cols-4 gap-1",
                  effects.glassmorphism,
                  "p-1 rounded-md"
                )}>
                  <TabsTrigger 
                    value="transkripsjon" 
                    className={cn(
                      "flex items-center justify-center",
                      "data-[state=active]:bg-gradient-to-r",
                      "data-[state=active]:from-blue-500",
                      "data-[state=active]:to-indigo-500",
                      "data-[state=active]:text-white rounded-md"
                    )}
                  >
                    <Mic className="h-4 w-4 mr-2" />
                    <span className="text-xs">Transkripsjon</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notat" 
                    className={cn(
                      "flex items-center justify-center",
                      "data-[state=active]:bg-gradient-to-r",
                      "data-[state=active]:from-blue-500",
                      "data-[state=active]:to-indigo-500",
                      "data-[state=active]:text-white rounded-md"
                    )}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    <span className="text-xs">Notat</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="maler" 
                    className={cn(
                      "flex items-center justify-center",
                      "data-[state=active]:bg-gradient-to-r",
                      "data-[state=active]:from-blue-500",
                      "data-[state=active]:to-indigo-500",
                      "data-[state=active]:text-white rounded-md"
                    )}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    <span className="text-xs">Maler</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="ressurser" 
                    className={cn(
                      "flex items-center justify-center",
                      "data-[state=active]:bg-gradient-to-r",
                      "data-[state=active]:from-blue-500",
                      "data-[state=active]:to-indigo-500",
                      "data-[state=active]:text-white rounded-md"
                    )}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    <span className="text-xs">Ressurser</span>
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="transkripsjon" className="flex-1 mt-4">
  <TranscriptionTab
    transcription={transcription}
    setTranscription={setTranscription}
    currentKonsultasjon={currentKonsultasjon}
    updateKonsultasjon={updateKonsultasjon}
    isRecording={isRecording}
    isTranscriptionLocked={isTranscriptionLocked}
    setIsTranscriptionLocked={setIsTranscriptionLocked}
    contextText={contextText}
    setContextText={setContextText}
    isLandscape={isLandscape}
    transcriptionLanguage={transcriptionLanguage}
    handleLanguageChange={handleLanguageChange}
    languageOptions={languageOptions}
    generateNote={generateNote}
    isGeneratingNote={isGeneratingNote}
    toggleRecordingForCurrent={toggleRecordingForCurrent}
    isTabletView={isTabletView}
    isDesktopView={isDesktopView}
  />
</TabsContent>
<TabsContent value="notat" className="flex-1 mt-4">
  <NoteTab
    note={note}
    chosenTemplate={chosenTemplate}
    setChosenTemplate={setChosenTemplate}
    templates={templates}
    generateNote={generateNote}
    isGeneratingNote={isGeneratingNote}
    convertMarkdownToHtml={convertMarkdownToHtml}
    setShowResourceSuggestions={setShowResourceSuggestions}
    isTabletView={isTabletView}
    isDesktopView={isDesktopView}
    isLandscape={isLandscape}
  />
</TabsContent>
        <TabsContent value="maler" className="flex-1 mt-4">
          <TemplateTab 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            templates={templates}
            setEditingTemplate={setEditingTemplate}
            handleDeleteTemplate={handleDeleteTemplate}
            setChosenTemplate={setChosenTemplate}
            setActiveTab={setActiveTab}
            isMobileView={false}
            setIsDialogOpen={setIsDialogOpen}
            isTabletView={isTabletView}
          />
        </TabsContent>

              
                <TabsContent value="ressurser" className="flex-1 mt-4">
                  <Card className={cn(
                    "h-[calc(100vh-220px)]",
                    componentStyles.card.glass
                  )}>
                    <CardContent className="p-0 h-full">
                      <ResourceBank />
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      )}
      {isSettingsOpen && (
        <SettingsDialog 
          open={isSettingsOpen} 
          onOpenChange={setIsSettingsOpen} 
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
        />
      )}
      {showResourceSuggestions && (
        <ResourceSuggestions 
          isOpen={showResourceSuggestions}
          onClose={() => setShowResourceSuggestions(false)}
          consultationText={transcription}
          consultationContext={contextText}
        />
      )}
      {isGeneratingNote && <LoadingSpinner />}

      <TemplateDialogForm
      isOpen={isDialogOpen}
      onClose={() => {
        setIsDialogOpen(false);
        setEditingTemplate(null);
      }}
      initialTemplate={editingTemplate}
      onSubmit={async (templateData) => {
        try {
          if (editingTemplate) {
            await handleUpdateTemplate(templateData.title, templateData.content, editingTemplate.title);
          } else {
            await handleUpdateTemplate(templateData.title, templateData.content);
          }
          setIsDialogOpen(false);
          setEditingTemplate(null);
        } catch (error) {
          console.error("Error saving template:", error);
        }
      }}
      isEditing={!!editingTemplate}
      isSaving={isTemplateSaving}
      />

      {showUsageLimitModal && currentUser && (
        <UsageLimitModal
          limitType={limitType}
          onClose={() => setShowUsageLimitModal(false)}
          currentUser={currentUser}
        />
      )}
      </div>
      </div>
      );
}


// Hovedkomponent med DesignSystemProvider
function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', isDarkMode.toString());
  }, [isDarkMode]);

  return (
    <DesignSystemProvider isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}>
      {/* 
        Applikasjonen er nå optimalisert for fire hovedtyper av skjermer:
        1. Mobilvisning portrett (< 640px, høyde > bredde): Kompakt layout med stablede elementer
        2. Mobilvisning landskap (< 1024px, bredde > høyde): Spesielt tilpasset layout for liggende mobiler
        3. Tabletvisning (640px-1024px, høyde > bredde): Mellomstørrelse med tilpasset layout
        4. Desktopvisning (> 1024px): Full layout med flere kolonner
        
        Viktige tilpasninger for mellomstore skjermer:
        - Redusert padding og spacing
        - Mindre fontstørrelser på knapper og overskrifter
        - Smalere sidebar (w-64 i stedet for w-80)
        - Tilpasset grid-layout for ressurser og maler
        - Optimalisert dialogstørrelser

        Viktige tilpasninger for liggende mobilvisning:
        - Redusert høyde på alle komponenter (header, cards, footer)
        - Fjernet ikke-kritiske UI-elementer
        - Omplassert handlingsknapper horisontalt i stedet for vertikalt
        - Grid med to kolonner for konsultasjonslisten
        - Mindre tekst og ikoner
        - Optimal høydeutnyttelse i dialogbokser
      */}
      <AppContent />
    </DesignSystemProvider>
    
  );
}

export default App;